import { getDoc, setDoc } from './store';
import type { Hit, Oab, Profile } from './types';
import type { Pub } from '../modules/monitor/djen';
import { uid } from '../lib/date';
import { supabase } from '../lib/supabase';
import { paraLixeira } from '../lib/lixeira';

/** Dias até a publicação descartada ser excluída em definitivo. */
export const DIAS_DESCARTE = 30;

/** Todas as OABs do perfil (lista nova + formato antigo, sem repetir). */
export function oabsDe(p: Profile): Oab[] {
  const lista: Oab[] = [...(p.oabs ?? [])];
  if (p.oab && p.oab_uf) lista.unshift({ numero: p.oab, uf: p.oab_uf });
  const vistos = new Set<string>();
  return lista.filter(o => {
    const n = String(o?.numero ?? '').replace(/\D/g, '').replace(/^0+/, '');
    const k = `${n}/${o?.uf}`;
    if (!n || !o?.uf || vistos.has(k)) return false;
    vistos.add(k); return true;
  });
}

type NovoPerfil = Pick<Profile, 'kind' | 'label' | 'name'> & { oabs: Oab[]; tribunais: string[]; diarios: boolean };

export const addProfile = (p: NovoPerfil) =>
  setDoc('monitor', d => ({ ...d, profiles: [...d.profiles, {
    id: uid(), kind: p.kind, label: p.label.trim(), name: p.name.trim(), last_check: null,
    oab: p.oabs[0]?.numero ?? null, oab_uf: p.oabs[0]?.uf ?? null,       // versões antigas do app leem estes
    oabs: p.oabs, tribunais: p.tribunais, diarios: p.diarios,
  }] }));

export const updateProfile = (id: string, p: NovoPerfil) =>
  setDoc('monitor', d => ({ ...d, profiles: d.profiles.map(x => (x.id !== id ? x : {
    ...x, kind: p.kind, label: p.label.trim(), name: p.name.trim(),
    oab: p.oabs[0]?.numero ?? null, oab_uf: p.oabs[0]?.uf ?? null,
    oabs: p.oabs, tribunais: p.tribunais, diarios: p.diarios,
  })) }));

export const removeProfile = (id: string) =>
  setDoc('monitor', d => ({ ...d, profiles: d.profiles.filter(p => p.id !== id), hits: d.hits.filter(h => h.profile_id !== id) }));

/** Remover pela tela (texto 05 C): o perfil e os resultados dele vão para a Lixeira, com o Desfazer. */
export function removerPerfil(id: string) {
  const d = getDoc('monitor');
  const pos = d.profiles.findIndex(p => p.id === id);
  if (pos < 0) return;
  const p = d.profiles[pos];
  paraLixeira('monitor', 'monitor-perfil', p.label || p.name, { perfil: p, hits: d.hits.filter(h => h.profile_id === id) }, { pos, grande: true });   // item grande: na Lixeira pede a senha
  removeProfile(id);
}
/** Volta o perfil (no mesmo lugar) e os resultados dele. */
export function voltarPerfil(x: { perfil: Profile; hits: Hit[] }, pos?: number) {
  if (!x?.perfil?.id) return;
  setDoc('monitor', d => {
    if (d.profiles.some(p => p.id === x.perfil.id)) return d;
    const i = pos === undefined || pos < 0 || pos > d.profiles.length ? d.profiles.length : pos;
    const novos = (x.hits ?? []).filter(h => !d.hits.some(y => y.hash === h.hash && y.profile_id === h.profile_id));
    return { ...d, profiles: [...d.profiles.slice(0, i), x.perfil, ...d.profiles.slice(i)], hits: caber([...d.hits, ...novos]) };
  });
}

export const touchProfile = (id: string) =>
  setDoc('monitor', d => ({ ...d, profiles: d.profiles.map(p => (p.id === id ? { ...p, last_check: new Date().toISOString() } : p)) }));

/** Limite de publicações guardadas. Ao passar, saem primeiro as descartadas, depois as mais antigas já tratadas;
 *  as NOVAS (ainda não tratadas) nunca saem por causa do limite (0.55). */
const MAX_HITS = 600;
function caber(hits: Hit[]): Hit[] {
  if (hits.length <= MAX_HITS) return hits;
  const sobra = hits.length - MAX_HITS;
  const ordem = (h: Hit) => (h.status === 'dismissed' ? 0 : h.status === 'confirmed' ? 1 : 2);
  const sair = new Set(hits.map((h, i) => ({ h, i })).filter(x => x.h.status !== 'new')
    .sort((a, b) => ordem(a.h) - ordem(b.h) || (a.h.found_at ?? '').localeCompare(b.h.found_at ?? ''))
    .slice(0, sobra).map(x => x.i));
  return hits.filter((_, i) => !sair.has(i));
}

/** Guarda as publicações novas (ignora as já conhecidas). As novas sempre entram, tenha ou não outras para tratar. */
export function saveHits(profileId: string, pubs: Pub[]) {
  completarHits(profileId, pubs);
  const known = new Set(getDoc('monitor').hits.filter(h => h.profile_id === profileId).map(h => h.hash));
  const novos: Hit[] = [];
  for (const p of pubs) {
    if (!p.hash || known.has(p.hash)) continue;
    known.add(p.hash);
    novos.push({ ...p, profile_id: profileId, status: 'new' as const, found_at: new Date().toISOString() });
  }
  if (novos.length) setDoc('monitor', d => ({ ...d, hits: caber([...novos, ...d.hits]) }));
  return novos.length;
}

export const setHitStatus = (hash: string, profileId: string, status: Hit['status'], taskId?: string) =>
  setDoc('monitor', d => ({ ...d, hits: d.hits.map(h => {
    if (h.hash !== hash || h.profile_id !== profileId) return h;
    const { dismissedAt, ...resto } = h;
    return { ...resto, status, ...(taskId !== undefined ? { taskId } : {}),
      ...(status === 'dismissed' ? { dismissedAt: dismissedAt ?? new Date().toISOString() } : {}) };
  }) }));

/** Altera uma publicação (todas as cópias com o mesmo hash e perfil). */
export const atualizarHit = (hash: string, profileId: string, fn: (h: Hit) => Hit) =>
  setDoc('monitor', d => ({ ...d, hits: d.hits.map(h => (h.hash === hash && h.profile_id === profileId ? fn(h) : h)) }));

/** Dias que faltam para uma publicação descartada ser excluída (0 = hoje). */
export function diasParaExcluir(h: Hit, agora = Date.now()) {
  if (!h.dismissedAt) return DIAS_DESCARTE;
  const passados = Math.floor((agora - new Date(h.dismissedAt).getTime()) / 864e5);
  return Math.max(0, DIAS_DESCARTE - passados);
}

/** Atualiza dados extras (partes, órgão...) de publicações já guardadas, quando a consulta traz mais informação. */
export function completarHits(profileId: string, pubs: Pub[]) {
  const porHash = new Map(pubs.map(p => [p.hash, p]));
  const precisa = getDoc('monitor').hits.some(h => h.profile_id === profileId && !h.partes && porHash.get(h.hash)?.partes);
  if (!precisa) return;
  setDoc('monitor', d => ({ ...d, hits: d.hits.map(h => {
    const p = h.profile_id === profileId && !h.partes ? porHash.get(h.hash) : undefined;
    return p?.partes ? { ...h, orgao: p.orgao, classe: p.classe, tipoDoc: p.tipoDoc, partes: p.partes, advogados: p.advogados } : h;
  }) }));
}

/**
 * Manutenção do documento: passa a OAB do formato antigo para a lista, data as descartadas sem data
 * e exclui em definitivo as descartadas há mais de 30 dias. Só grava se algo mudou.
 */
export function limparMonitor() {
  const m = getDoc('monitor');
  const agora = Date.now();
  const iso = new Date(agora).toISOString();
  const migrar = m.profiles.some(p => !p.oabs || (p.oab && p.oab_uf && !p.oabs.length));
  const datar = m.hits.some(h => h.status === 'dismissed' && !h.dismissedAt);
  const vencidas = m.hits.some(h => h.status === 'dismissed' && h.dismissedAt && agora - new Date(h.dismissedAt).getTime() >= DIAS_DESCARTE * 864e5);
  const soltas = m.hits.some(h => h.status !== 'dismissed' && h.dismissedAt);
  if (!migrar && !datar && !vencidas && !soltas) return;
  setDoc('monitor', d => ({
    ...d,
    profiles: d.profiles.map(p => (p.oabs && !(p.oab && p.oab_uf && !p.oabs.length) ? p : { ...p, oabs: oabsDe(p) })),
    hits: d.hits
      .map((h): Hit => {
        if (h.status === 'dismissed') return h.dismissedAt ? h : { ...h, dismissedAt: iso };
        if (!h.dismissedAt) return h;
        const { dismissedAt: _x, ...resto } = h; return resto;
      })
      .filter(h => !(h.status === 'dismissed' && h.dismissedAt && agora - new Date(h.dismissedAt).getTime() >= DIAS_DESCARTE * 864e5)),
  }));
}

export const giveConsent = () => setDoc('monitor', d => ({ ...d, consent: true }));

export async function maybeAutoCheck(run: () => Promise<unknown>) {
  limparMonitor();
  const m = getDoc('monitor');
  if (!m.consent || !m.profiles.length || Date.now() - m.lastAuto < 6 * 3600 * 1000) return;
  setDoc('monitor', d => ({ ...d, lastAuto: Date.now() }));
  await run();
}

// ─── Captura automática do servidor (função "monitor-djen", 08h e 12h) ────────────────────────
let puxando: Promise<number> | null = null;

/**
 * Traz as publicações que o servidor capturou (tabela monitor_capturas), junta ao documento sem
 * duplicar e apaga as linhas trazidas. Devolve quantas publicações novas entraram.
 * Chame depois de sincronizar, para juntar sobre a versão mais recente do documento.
 */
export function puxarCapturas(): Promise<number> {
  if (puxando) return puxando;
  puxando = (async () => {
    limparMonitor();
    const m = getDoc('monitor');
    if (!supabase || !m.consent) return 0;
    let novos = 0;
    for (let volta = 0; volta < 5; volta++) {
      const conhecidos = getDoc('monitor').profiles.map(p => p.id);
      if (!conhecidos.length) break;
      const { data, error } = await supabase.from('monitor_capturas')
        .select('id, profile_id, hash, dados').in('profile_id', conhecidos).order('id', { ascending: true }).limit(300);
      if (error || !data?.length) break;
      const perfis = new Set(getDoc('monitor').profiles.map(p => p.id));
      const porPerfil = new Map<string, Pub[]>();
      for (const r of data as { profile_id: string; hash: string; dados: any }[]) {
        if (!perfis.has(r.profile_id) || !r.dados || typeof r.dados !== 'object') continue;
        const d = r.dados;
        const pub: Pub = {
          hash: String(d.hash ?? r.hash), process: String(d.process ?? ''), court: String(d.court ?? ''), kind: String(d.kind ?? ''),
          available: String(d.available ?? ''), text: String(d.text ?? ''), link: String(d.link ?? ''),
          ...(d.orgao ? { orgao: String(d.orgao) } : {}), ...(d.classe ? { classe: String(d.classe) } : {}),
          ...(d.tipoDoc ? { tipoDoc: String(d.tipoDoc) } : {}),
          ...(Array.isArray(d.partes) ? { partes: d.partes } : {}), ...(Array.isArray(d.advogados) ? { advogados: d.advogados } : {}),
        };
        porPerfil.set(r.profile_id, [...(porPerfil.get(r.profile_id) ?? []), pub]);
      }
      for (const [pid, pubs] of porPerfil) novos += saveHits(pid, pubs);
      // 0.55: só apaga do servidor o que foi guardado aqui. Captura de um perfil que este aparelho ainda
      // não carregou (app acabando de abrir, outro aparelho) fica lá para a próxima vez, em vez de se perder.
      const guardadas = (data as { id: number; profile_id: string }[]).filter(r => perfis.has(r.profile_id)).map(r => r.id);
      if (!guardadas.length) break;
      const { error: e2 } = await supabase.from('monitor_capturas').delete().in('id', guardadas);
      if (e2 || data.length < 300) break;
    }
    return novos;
  })().catch(() => 0).finally(() => { puxando = null; });
  return puxando;
}
